/**
 * EngineDetailViews — Phase 07.5
 * ================================
 *
 * Per-engine display wrappers that render inside CalculationReviewPanel's
 * children slot. Each wrapper:
 *   1. Loads the report bundle (calc_detail + result) via the collaboration view endpoint
 *   2. Slices the bundle into the shape its display component expects
 *   3. Falls back to an "engine output not yet available" notice if the slice is missing
 *
 * The merchant_reconciliation engine has its own bespoke wrapper inside
 * AccountantEngineReviewDetail.tsx (predates this file). Everything else lives here.
 *
 * Auth: each wrapper reads useAccountantAuth().getAuthHeader via the guarded
 * `useAuthHeader` helper (GAP-P1-ROLECAP: optional read — outside
 * AccountantAuthProvider it degrades to an empty-header function instead of
 * throwing, so attorney/adjuster/data-specialist routes can render these
 * views without the accountant JWT context) and passes it down to the few
 * display components that fetch their own data (FairSettlementSection,
 * AttorneyAnalysisSection, ComparativeAnalysisSection).
 *
 * In `in_house_*` modes the same wrappers are used; getAuthHeader returns
 * `{}` if no JWT is present, which forces the underlying fetch to use the
 * Clerk-injected interceptor in `utils/api.ts`.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowUpRight, ArrowDownRight, Construction, AlertTriangle } from 'lucide-react';

import { useAccountantAuth } from '../../../contexts/AccountantAuthContext';
import { useCanonicalNet } from '../../../hooks/useCanonicalNet';
import { api } from '../../../utils/api';
import {
  getReportViewExternal,
  getReportViewInHouse,
} from '../../../services/engineReviewApi';

import {
  BenchmarkPeriodCard,
  VariableProfitAnalysisCard,
  GrowthAdjustmentCard,
  FinalCalculationCard,
  SensitivityAnalysisSection,
  FairSettlementSection,
  PayrollReconciliationCard,
} from '../../accountant';

import {
  ENGINE_NAMES,
  ENGINE_DESCRIPTIONS,
  ENGINE_DEPENDENCIES,
  ENGINE_DOWNSTREAM,
  type EngineId,
  type ReviewPanelMode,
} from '../../../types/engineReview';

import { formatCurrency } from '../../../utils/financialFormat';
import MitigationAttestationBoard from './MitigationAttestationBoard';

// =============================================================================
// SHARED FORMATTERS
// =============================================================================

const formatPercent = (value?: number): string => {
  if (value === undefined || value === null || Number.isNaN(value)) return 'N/A';
  return `${value.toFixed(2)}%`;
};

// =============================================================================
// SHARED HOOK — fetch the report bundle once per wrapper mount
// =============================================================================

interface ReportBundle {
  id: string;
  client_name?: string;
  business_type?: string;
  financial_data?: any;
  result?: {
    final_claim_amount?: number;
    revenue_loss?: number;
    calculation_detail?: any;
    payroll_reconciliation?: any;
    [key: string]: any;
  };
  [key: string]: any;
}

interface UseEngineReportData {
  report: ReportBundle | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

function useEngineReportData(
  reportId: string,
  refreshKey: number = 0,
  mode: ReviewPanelMode = 'in_house_readonly',
): UseEngineReportData {
  const [report, setReport] = useState<ReportBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // TS-FU12 fix: mode-aware dispatch. External (witness CPA) hits
      // the witness-JWT-gated `/collaboration/reports/{id}/view`. Any
      // in_house mode hits the new Clerk-gated `/engine-review/view/{id}`
      // shipped as part of TS-FU12. Pre-fix, both modes called the
      // witness route → in-house users got 401 "Invalid authentication
      // token" on every non-merchant engine.
      const fetchFn =
        mode === 'external' ? getReportViewExternal : getReportViewInHouse;
      const data = await fetchFn(reportId);
      setReport(data as ReportBundle);
    } catch (err: any) {
      setError(err?.message || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [reportId, mode]);

  // Phase 07.3c: when the parent panel records a state transition
  // (approve/dispute/waive/start-review) it bumps `refreshKey` so the
  // bundle re-fetches. Without this, the `result.calculation_detail`
  // slice can stay stale for any engine routed through EngineDetailView
  // until the user manually reloads the page.
  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return { report, loading, error, reload: load };
}

// =============================================================================
// SHARED UI ATOMS
// =============================================================================

function SectionLoading() {
  return (
    <div className="space-y-4 animate-pulse" data-testid="engine-detail-loading">
      <div className="h-6 w-56 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }} />
      <div className="h-32 rounded-lg" style={{ backgroundColor: 'var(--color-bg-hover)' }} />
      <div className="h-48 rounded-lg" style={{ backgroundColor: 'var(--color-bg-hover)' }} />
    </div>
  );
}

function SectionError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      className="border rounded-[2px] p-4 text-sm flex items-start gap-3"
      style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}
      data-testid="engine-detail-error"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-blocker)' }} />
      <div className="flex-1">
        <p className="font-medium" style={{ color: 'var(--color-blocker)' }}>Could not load engine data</p>
        <p className="mt-1 text-xs" style={{ color: 'var(--color-text-secondary)' }}>{message}</p>
      </div>
      <button
        onClick={onRetry}
        className="text-xs underline flex-shrink-0"
        style={{ color: 'var(--color-brand)' }}
      >
        Retry
      </button>
    </div>
  );
}

function MissingEngineOutput({ engineId, hint }: { engineId: EngineId; hint?: string }) {
  const upstream = ENGINE_DEPENDENCIES[engineId];
  const downstream = ENGINE_DOWNSTREAM[engineId];

  return (
    <div
      className="border border-dashed rounded-[2px] p-6"
      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}
      data-testid="engine-output-missing"
    >
      <div className="flex items-start gap-3 mb-3">
        <Construction className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-text-muted)' }} />
        <div>
          <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Output not available yet
          </p>
          <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
            {hint ||
              'This engine has not produced output for the current run, or the upstream calculation has not completed.'}
          </p>
          <p className="text-xs mt-2 italic" style={{ color: 'var(--color-text-dim)' }}>
            {ENGINE_DESCRIPTIONS[engineId]}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 pt-4 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
        <div data-testid="missing-upstream">
          <div className="flex items-center gap-1.5 text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-primary)' }}>
            <ArrowUpRight className="w-3.5 h-3.5" />
            Upstream inputs
          </div>
          {upstream.length === 0 ? (
            <p className="text-xs italic" style={{ color: 'var(--color-text-dim)' }}>Root engine — no upstream dependencies</p>
          ) : (
            <ul className="space-y-1">
              {upstream.map((id) => (
                <li key={id} className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  • {ENGINE_NAMES[id]}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div data-testid="missing-downstream">
          <div className="flex items-center gap-1.5 text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-primary)' }}>
            <ArrowDownRight className="w-3.5 h-3.5" />
            Downstream consumers
          </div>
          {downstream.length === 0 ? (
            <p className="text-xs italic" style={{ color: 'var(--color-text-dim)' }}>Terminal engine — no downstream consumers</p>
          ) : (
            <ul className="space-y-1">
              {downstream.map((id) => (
                <li key={id} className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  • {ENGINE_NAMES[id]}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// COMPOSE-ONLY WRAPPERS — engines whose data lives in result.calculation_detail
// =============================================================================

interface EngineDetailProps {
  reportId: string;
  /** Bumped by parent on every state transition so children re-fetch. */
  refreshKey?: number;
  /** TS-FU12: threaded into `useEngineReportData` so the data fetch picks the
   * right namespace per mode. Defaults to `in_house_readonly` so call sites
   * that haven't been retrofitted still work. */
  mode?: ReviewPanelMode;
}

export function BottomUpDetail({ reportId, refreshKey, mode }: EngineDetailProps) {
  const { report, loading, error, reload } = useEngineReportData(reportId, refreshKey, mode);
  const getAuthHeader = useAuthHeader();

  if (loading) return <SectionLoading />;
  if (error) return <SectionError message={error} onRetry={reload} />;

  const calcDetail = report?.result?.calculation_detail;
  const benchmark = calcDetail?.benchmark_period;
  const variableProfit = calcDetail?.variable_profit_analysis;
  const finalCalc = calcDetail?.final_calculation;
  // Payroll reconciliation is a BI-input artifact (payroll feeds variable
  // profit), persisted at report.result.payroll_reconciliation by the generate
  // pipeline. It rendered on the main report view but never on this engine-
  // review surface — surface it here alongside the bottom-up breakdown.
  const payrollReconciliation = report?.result?.payroll_reconciliation;

  if (!benchmark && !variableProfit && !finalCalc && !payrollReconciliation) {
    return <MissingEngineOutput engineId="bottom_up" />;
  }

  return (
    <div className="space-y-6" data-testid="engine-detail-bottom_up">
      {benchmark && (
        <BenchmarkPeriodCard benchmarkPeriod={benchmark} formatCurrency={formatCurrency} />
      )}
      {variableProfit && (
        <VariableProfitAnalysisCard
          variableProfitAnalysis={variableProfit}
          benchmarkPeriod={benchmark}
          formatCurrency={formatCurrency}
          formatPercent={formatPercent}
        />
      )}
      {finalCalc && (
        <FinalCalculationCard finalCalculation={finalCalc} formatCurrency={formatCurrency} />
      )}
      {payrollReconciliation && (
        <PayrollReconciliationCard
          payrollReconciliation={payrollReconciliation}
          reportId={reportId}
          canComment={false}
          getAuthHeader={getAuthHeader}
          formatCurrency={formatCurrency}
          formatPercent={formatPercent}
        />
      )}
    </div>
  );
}

export function EnhancedClaimDetail({ reportId, refreshKey, mode }: EngineDetailProps) {
  const { report, loading, error, reload } = useEngineReportData(reportId, refreshKey, mode);

  if (loading) return <SectionLoading />;
  if (error) return <SectionError message={error} onRetry={reload} />;

  const calcDetail = report?.result?.calculation_detail;
  const benchmark = calcDetail?.benchmark_period;
  const growthAdj = calcDetail?.growth_adjustment;

  if (!benchmark && !growthAdj) {
    return <MissingEngineOutput engineId="enhanced_claim" />;
  }

  return (
    <div className="space-y-6" data-testid="engine-detail-enhanced_claim">
      {benchmark && (
        <BenchmarkPeriodCard benchmarkPeriod={benchmark} formatCurrency={formatCurrency} />
      )}
      {growthAdj && (
        <GrowthAdjustmentCard
          growthAdjustment={growthAdj}
          benchmarkRevenue={benchmark?.revenue}
          variableMarginPct={calcDetail?.variable_profit_analysis?.variable_margin_pct}
          financialData={report?.financial_data}
          formatCurrency={formatCurrency}
          formatPercent={formatPercent}
        />
      )}
    </div>
  );
}

export function MerchantAugmentationDetail({ reportId, refreshKey, mode }: EngineDetailProps) {
  const { report, loading, error, reload } = useEngineReportData(reportId, refreshKey, mode);

  if (loading) return <SectionLoading />;
  if (error) return <SectionError message={error} onRetry={reload} />;

  const calcDetail = report?.result?.calculation_detail;
  const growthAdj = calcDetail?.growth_adjustment;

  if (!growthAdj) {
    return (
      <MissingEngineOutput
        engineId="merchant_revenue_augmentation"
        hint="The merchant revenue augmentation engine has not yet produced a growth adjustment for this claim."
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="engine-detail-merchant_revenue_augmentation">
      <GrowthAdjustmentCard
        growthAdjustment={growthAdj}
        benchmarkRevenue={calcDetail?.benchmark_period?.revenue}
        variableMarginPct={calcDetail?.variable_profit_analysis?.variable_margin_pct}
        financialData={report?.financial_data}
        formatCurrency={formatCurrency}
        formatPercent={formatPercent}
      />
    </div>
  );
}

// =============================================================================
// COMPONENTS THAT FETCH THEIR OWN DATA (need getAuthHeader)
// =============================================================================

const EMPTY_AUTH_HEADER: () => { Authorization: string } | {} = () => ({});

function useAuthHeader(): () => { Authorization: string } | {} {
  // GAP-P1-ROLECAP: these detail views now also render on non-accountant
  // routes (attorney / adjuster / data specialist) where
  // AccountantAuthProvider is NOT mounted and useAccountantAuth throws.
  // Guarded read (same guarded-hook pattern as useWorkspaceRole): fall back
  // to an empty header, which forces the underlying fetches through the
  // Clerk-injected interceptor in `utils/api.ts` — the exact degradation
  // the in_house_* modes already rely on when no JWT is present (see the
  // file header note above). Accountant behavior is unchanged: with the
  // provider mounted, the same getAuthHeader is returned as before.
  try {
    const { getAuthHeader } = useAccountantAuth();
    return getAuthHeader;
  } catch {
    return EMPTY_AUTH_HEADER;
  }
}

// NOTE: dual_method + case_strength now render through DynamicEngineDetail (they read
// their own engine_outputs via /engine-review/{id}/{engineId}/output). The bespoke
// DualMethodDetail (witness-only /comparative-analysis, 401 in-house) and CaseStrengthDetail
// (attorney-analyze, 404 on drafts) were removed — see the dispatch switch below.

export function FairSettlementDetail({ reportId, refreshKey, mode }: EngineDetailProps) {
  const { report, loading, error, reload } = useEngineReportData(reportId, refreshKey, mode);
  const getAuthHeader = useAuthHeader();
  // Canonical sworn net (policy_deductions.net_claim); snapshot is fallback only.
  const canonicalNet = useCanonicalNet(reportId);

  if (loading) return <SectionLoading />;
  if (error) return <SectionError message={error} onRetry={reload} />;

  const finalClaimAmount = canonicalNet.net ?? report?.result?.final_claim_amount ?? 0;

  return (
    <div className="space-y-6" data-testid="engine-detail-fair_settlement">
      <FairSettlementSection
        reportId={reportId}
        getAuthHeader={getAuthHeader}
        formatCurrency={formatCurrency}
      />
      {finalClaimAmount > 0 && (
        <SensitivityAnalysisSection
          sensitivityAnalysis={(report as any)?.sensitivity_analysis ?? null}
          reportId={reportId}
          formatCurrency={formatCurrency}
        />
      )}
    </div>
  );
}

// =============================================================================
// ENGINES WITHOUT EXISTING DISPLAY COMPONENTS
//
// For Phase 07.5 these surface a structured summary of the calculation_detail
// slice plus the same DAG context the placeholder used. A full purpose-built
// card per engine is deferred — the data shape from the deterministic engines
// is still settling and the verifier hash chain hasn't graduated past
// dual_method downstream. See docs/plans/2026-04-22-accountant-attestation/03-plan.md
// §"Phase 07.5 — Wiring engine display components" for the deferral note.
// =============================================================================

interface SimpleSummaryProps {
  engineId: EngineId;
  reportId: string;
  /** Bumped by parent on every state transition so the bundle re-fetches. */
  refreshKey?: number;
  // dotted-path slices to surface from result.calculation_detail (or result root)
  fields: Array<{ label: string; path: string; format?: 'currency' | 'percent' | 'text' }>;
  /** TS-FU12: threaded into useEngineReportData so the data fetch picks the
   * right namespace per mode. */
  mode?: ReviewPanelMode;
}

function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

// =============================================================================
// COMPILE-TIME TYPE PINS
// =============================================================================
// These constants pin the field-path strings in the 5 supplemental engine
// cards (Mitigation, Impact, EPI, PolicyDeductions, PV) against the
// auto-generated `EngineReviewDetail` schema from the backend OpenAPI spec.
//
// They are zero-runtime-cost compile-time assertions: the `keyof` constraint
// forces tsc to verify every string here against the generated type. A
// backend rename like `MitigationDetail.total_mitigation` → `mitigation_total`
// regenerates `api.ts`, removes `'total_mitigation'` from the keyof union,
// and breaks tsc IN THIS FILE — pointing to the bad string instead of
// silently degrading the rendered card to MissingEngineOutput.
//
// The 5 wrappers below (MitigationDetail/ImpactDetail/EPIDetail/
// PolicyDeductionsDetail/PVDetail) still use `getNestedValue(..., 'a.b')`
// for runtime resolution. The pins guarantee the strings stay in sync
// with the canonical Pydantic shapes.
//
// Why this voids: the `_PIN` constants are intentionally unused at
// runtime — `void X` references them so tsc keeps them in scope. They
// cost zero bytes after tree-shaking.
import type { components } from '../../../types/generated/api';

type EngineReviewDetailSchema = components['schemas']['EngineReviewDetail'];

// Slot-key pin: every key referenced in the 5 wrappers MUST be a valid
// slot of EngineReviewDetail.
const _ENGINE_SLOT_PIN: ReadonlyArray<keyof EngineReviewDetailSchema> = [
  'mitigation',
  'impact_period',
  'epi',
  'policy_deductions',
  'present_value',
] as const;
void _ENGINE_SLOT_PIN;

// Per-slot field pins — same trick at field level. A canonical rename
// in any Pydantic detail model fails tsc here, with the bad string in
// the error message.
const _MITIGATION_FIELD_PIN: ReadonlyArray<
  keyof NonNullable<EngineReviewDetailSchema['mitigation']>
> = ['total_mitigation', 'method', 'adjusted_loss'] as const;
const _IMPACT_FIELD_PIN: ReadonlyArray<
  keyof NonNullable<EngineReviewDetailSchema['impact_period']>
> = ['start', 'end', 'days', 'indemnity_days'] as const;
const _EPI_FIELD_PIN: ReadonlyArray<
  keyof NonNullable<EngineReviewDetailSchema['epi']>
> = ['amount', 'period', 'justification'] as const;
const _POLICY_FIELD_PIN: ReadonlyArray<
  keyof NonNullable<EngineReviewDetailSchema['policy_deductions']>
> = ['deductible', 'coinsurance_pct', 'policy_limit', 'net_amount'] as const;
const _PV_FIELD_PIN: ReadonlyArray<
  keyof NonNullable<EngineReviewDetailSchema['present_value']>
> = ['discount_rate', 'pv_amount', 'undiscounted_total'] as const;
void _MITIGATION_FIELD_PIN;
void _IMPACT_FIELD_PIN;
void _EPI_FIELD_PIN;
void _POLICY_FIELD_PIN;
void _PV_FIELD_PIN;

function CalculationDetailSummary({ engineId, reportId, fields, refreshKey, mode }: SimpleSummaryProps) {
  const { report, loading, error, reload } = useEngineReportData(reportId, refreshKey, mode);

  if (loading) return <SectionLoading />;
  if (error) return <SectionError message={error} onRetry={reload} />;

  const calcDetail = report?.result?.calculation_detail;
  const result = report?.result;

  const populated = fields
    .map((f) => {
      const calcVal = calcDetail ? getNestedValue(calcDetail, f.path) : undefined;
      const resultVal = result ? getNestedValue(result, f.path) : undefined;
      const value = calcVal !== undefined ? calcVal : resultVal;
      return { ...f, value };
    })
    .filter((f) => f.value !== undefined && f.value !== null);

  if (populated.length === 0) {
    return <MissingEngineOutput engineId={engineId} />;
  }

  const renderValue = (
    value: any,
    format: 'currency' | 'percent' | 'text' | undefined,
  ): string => {
    if (format === 'currency') return formatCurrency(typeof value === 'number' ? value : Number(value));
    if (format === 'percent') return formatPercent(typeof value === 'number' ? value : Number(value));
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  return (
    <div
      className="border rounded-lg p-5 space-y-3"
      style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
      data-testid={`engine-detail-${engineId}`}
    >
      {/* Engine name lives in the page H1; the card leads with its description
          to avoid duplicating the title two rows down. */}
      <div className="flex items-start gap-3 pb-3 border-b" style={{ borderColor: 'var(--color-border)' }}>
        <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
          {ENGINE_DESCRIPTIONS[engineId]}
        </p>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        {populated.map((f) => (
          <div key={f.path} className="flex flex-col gap-0.5">
            <dt className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-label)' }}>
              {f.label}
            </dt>
            <dd
              className={`text-sm ${
                f.format === 'currency' || f.format === 'percent' ? 'font-mono tabular-nums' : ''
              }`}
              style={{ color: 'var(--color-text-primary)' }}
            >
              {renderValue(f.value, f.format)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function MitigationDetail({ reportId, refreshKey, mode }: EngineDetailProps) {
  return (
    <div className="flex flex-col gap-3">
      <CalculationDetailSummary
        engineId="mitigation"
        reportId={reportId}
        refreshKey={refreshKey}
        mode={mode}
        fields={[
          { label: 'Mitigation amount', path: 'mitigation.total_mitigation', format: 'currency' },
          { label: 'Mitigation method', path: 'mitigation.method', format: 'text' },
          { label: 'Adjusted loss', path: 'mitigation.adjusted_loss', format: 'currency' },
        ]}
      />
      {/* CP 00 30 per-lever attest/clear — the CPA's evidence-gated hand on the
          mediation neutral. Reads the same engine Cᵢ the summary shows; writes p. */}
      <MitigationAttestationBoard reportId={reportId} />
    </div>
  );
}

export function ImpactDetail({ reportId, refreshKey, mode }: EngineDetailProps) {
  return (
    <CalculationDetailSummary
      engineId="impact"
      reportId={reportId}
      refreshKey={refreshKey}
      mode={mode}
      fields={[
        { label: 'Impact period start', path: 'impact_period.start', format: 'text' },
        { label: 'Impact period end', path: 'impact_period.end', format: 'text' },
        { label: 'Impact period (days)', path: 'impact_period.days', format: 'text' },
        { label: 'Indemnity period applied', path: 'impact_period.indemnity_days', format: 'text' },
      ]}
    />
  );
}

export function EPIDetail({ reportId, refreshKey, mode }: EngineDetailProps) {
  return (
    <CalculationDetailSummary
      engineId="epi"
      reportId={reportId}
      refreshKey={refreshKey}
      mode={mode}
      fields={[
        { label: 'EPI amount', path: 'epi.amount', format: 'currency' },
        { label: 'EPI period', path: 'epi.period', format: 'text' },
        // Coverage status / contested reason surface only when the engine flags them
        // (CalculationDetailSummary drops null fields) — a visible caveat, not buried.
        { label: 'Coverage status', path: 'epi.status', format: 'text' },
        { label: 'Contested reason', path: 'epi.contested_reason', format: 'text' },
        { label: 'Justification', path: 'epi.justification', format: 'text' },
      ]}
    />
  );
}

export function PolicyDeductionsDetail({ reportId, refreshKey, mode }: EngineDetailProps) {
  return (
    <CalculationDetailSummary
      engineId="policy_deductions"
      reportId={reportId}
      refreshKey={refreshKey}
      mode={mode}
      fields={[
        { label: 'Deductible', path: 'policy_deductions.deductible', format: 'currency' },
        { label: 'Co-insurance %', path: 'policy_deductions.coinsurance_pct', format: 'percent' },
        // Waiting period (time deductible) — row is skipped when absent (null path).
        // A standard carrier dispute the accountant must be able to see + defend.
        { label: 'Waiting period', path: 'policy_deductions.waiting_period_note', format: 'text' },
        { label: 'Policy limit', path: 'policy_deductions.policy_limit', format: 'currency' },
        { label: 'Net of deductions', path: 'policy_deductions.net_amount', format: 'currency' },
      ]}
    />
  );
}

export function PVDetail({ reportId, refreshKey, mode }: EngineDetailProps) {
  return (
    <CalculationDetailSummary
      engineId="pv"
      reportId={reportId}
      refreshKey={refreshKey}
      mode={mode}
      fields={[
        { label: 'Discount rate', path: 'present_value.discount_rate', format: 'percent' },
        { label: 'PV amount', path: 'present_value.pv_amount', format: 'currency' },
        { label: 'Undiscounted total', path: 'present_value.undiscounted_total', format: 'currency' },
      ]}
    />
  );
}

// =============================================================================
// DYNAMIC DETAIL — methodology-loop + Group A deterministic engines
//
// These engines (projection_trend, two_period, capacity_ceiling,
// non_recurring_item_detector, catch_up_sales, monthly_limit_cap,
// extra_expense_cap) have no bespoke card yet. Instead of assuming field
// paths that may not match the real output shape (the exact trap the
// "verify against reality" rule exists for), this renders every populated
// top-level key of result.calculation_detail (falling back to result) as a
// label/value grid — the reviewer sees exactly what the engine produced.
// =============================================================================

function humanizeKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

interface EngineOutputResponse {
  report_id: string;
  engine_id: string;
  found: boolean;
  outputs: Record<string, unknown> | null;
  run_id?: string;
  timestamp?: string;
  stale?: boolean;
}

// Small typed helpers and constants for DynamicEngineDetail (K3 generic implementation)
type OutputRow = Record<string, unknown>;

const TABLE_HEADER_OVERRIDES: Record<string, string> = {
  prior_avg: 'Prior Avg',
  actual: 'Actual',
  loss: 'Loss',
  but_for_revenue: 'But-For Rev',
  actual_revenue: 'Actual Rev',
};

const SYSTEM_OUTPUT_KEYS = new Set([
  'engine',
  'run_id',
  'timestamp',
  'report_id',
  'engine_id',
  'found',
  'outputs',
]);

const NON_MONEY_KEY = /(pct|percent|ratio|score|count|days|month|year|slope|intercept|r_squared|p_value|cv|threshold|window|std|_z$|z_)/i;

const isMoneyKey = (k: string): boolean =>
  /(loss|amount|revenue|cost|credit|deduction|offset|ceiling|claim|gross|net|expense|payment|variance|prior_avg|actual|value_unlock|adjusted)/i.test(k)
  && !NON_MONEY_KEY.test(k);

const isNumeric = (v: unknown): v is number | string =>
  typeof v === 'number' || (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v));

const parseNumeric = (v: number | string): number =>
  typeof v === 'number' ? v : parseFloat(v);

const humanizeHeader = (k: string): string =>
  TABLE_HEADER_OVERRIDES[k] ?? humanizeKey(k);

const formatPeriod = (year: number | string, month: number | string): string => {
  const y = Number(year);
  const m = Number(month);
  if (Number.isNaN(y) || Number.isNaN(m) || m < 1 || m > 12) return `${year}-${String(month).padStart(2, '0')}`;
  const d = new Date(y, m - 1);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

export function DynamicEngineDetail({
  engineId,
  reportId,
  refreshKey,
  mode,
}: EngineDetailProps & { engineId: EngineId }) {
  const [data, setData] = useState<EngineOutputResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState(false);
  const [expandedTables, setExpandedTables] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<EngineOutputResponse>(
        `/api/v1/engine-review/${reportId}/${engineId}/output`
      );
      setData(res);
    } catch (err: any) {
      setError(err?.message || 'Failed to load engine output');
    } finally {
      setLoading(false);
    }
  }, [reportId, engineId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const toggleTable = useCallback((key: string) => {
    setExpandedTables(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  if (loading) return <SectionLoading />;
  if (error) return <SectionError message={error} onRetry={load} />;

  if (!data?.found || !data.outputs || Object.keys(data.outputs).length === 0) {
    return <MissingEngineOutput engineId={engineId} />;
  }

  const outputs = data.outputs;

  // Hero extraction: total_loss must be numeric (number or numeric string)
  const heroValue: number | null = (() => {
    const tl = outputs.total_loss;
    if (typeof tl === 'number' && !Number.isNaN(tl)) return tl;
    if (typeof tl === 'string' && /^-?\d+(\.\d+)?$/.test(tl)) return parseFloat(tl);
    return null;
  })();

  // Partition entries for grid vs tables
  const scalarEntries: Array<[string, unknown]> = [];
  const tableEntries: Array<[string, Array<OutputRow>]> = [];

  Object.entries(outputs).forEach(([k, v]) => {
    if (k === 'total_loss' && heroValue !== null) return; // exclude hero from grid
    if (SYSTEM_OUTPUT_KEYS.has(k)) return; // exclude system keys from grid

    if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object' && v[0] !== null) {
      tableEntries.push([k, v as Array<OutputRow>]);
    } else {
      scalarEntries.push([k, v]);
    }
  });

  const renderScalarValue = (key: string, value: unknown): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
    }

    if (typeof value === 'boolean') {
      return <span>{value ? 'Yes' : 'No'}</span>;
    }

    if (isNumeric(value)) {
      const n = parseNumeric(value);
      if (isMoneyKey(key)) {
        return <span className="font-mono tabular-nums">{formatCurrency(n)}</span>;
      }
      return <span className="font-mono tabular-nums">{Number.isInteger(n) ? String(n) : n.toFixed(2)}</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
      return (
        <span className="font-mono text-sm" style={{ color: 'var(--color-text-primary)' }}>
          {value.map((item, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <span style={{ color: 'var(--color-text-muted)', margin: '0 6px' }}>·</span>}
              <span>{String(item)}</span>
            </React.Fragment>
          ))}
        </span>
      );
    }

    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      return (
        <div className="space-y-1">
          {Object.entries(obj).map(([subKey, subVal]) => (
            <div key={subKey} className="flex justify-between gap-4 text-xs">
              <span style={{ color: 'var(--color-text-muted)' }}>{humanizeHeader(subKey)}</span>
              <span className="font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                {subVal === null ? '—' :
                 typeof subVal === 'boolean' ? (subVal ? 'Yes' : 'No') :
                 isNumeric(subVal) ? (isMoneyKey(subKey) ? formatCurrency(parseNumeric(subVal)) : parseNumeric(subVal).toFixed(2)) :
                 String(subVal)}
              </span>
            </div>
          ))}
        </div>
      );
    }

    return <span>{String(value)}</span>;
  };

  return (
    <div
      className="border rounded-lg"
      style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
      data-testid={`engine-detail-${engineId}`}
    >
      {/* Hero band */}
      {heroValue !== null && (
        <div className="px-6 pt-6 pb-2">
          <div 
            className="text-[10.5px] font-semibold uppercase tracking-[0.12em] mb-2" 
            style={{ color: 'var(--color-text-muted)' }}
          >
            Total Loss
          </div>
          <div 
            className="font-mono tabular-nums"
            style={{ fontSize: '28px', color: 'var(--color-text-primary)', lineHeight: 1.2 }}
          >
            {formatCurrency(heroValue)}
          </div>
        </div>
      )}

      {/* Card header — name lives in the page <h1>; here we keep only the
          stale-after-rerun chip + the engine description (K3 Defect B: the
          duplicate engine-name heading was removed). */}
      <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
        {data.stale && (
          <div className="mb-1.5">
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-[2px] border"
              style={{ color: 'var(--color-needs-human)', borderColor: 'var(--color-border-sub)' }}
            >
              engine version changed — re-run
            </span>
          </div>
        )}
        <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
          {ENGINE_DESCRIPTIONS[engineId]}
        </p>
      </div>

      {/* Scalar grid: 2 cols, label above, left-aligned */}
      {scalarEntries.length > 0 && (
        <div className="p-6 grid grid-cols-2 gap-x-8 gap-y-6">
          {scalarEntries.map(([key, value]) => (
            <div key={key} className="flex flex-col gap-1.5">
              <dt 
                className="text-xs font-medium uppercase tracking-wide" 
                style={{ color: 'var(--color-text-label)' }}
              >
                {humanizeHeader(key)}
              </dt>
              <dd className="text-[15px]" style={{ color: 'var(--color-text-primary)' }}>
                {renderScalarValue(key, value)}
              </dd>
            </div>
          ))}
        </div>
      )}

      {/* Object-array tables: full-width, below grid */}
      {tableEntries.map(([key, rows]) => {
        const isExpanded = expandedTables[key] ?? false;
        const displayRows = isExpanded ? rows : rows.slice(0, 10);
        const totalRows = rows.length;
        const showToggle = totalRows > 10;

        // Column derivation: union of keys, first-seen order. Exclude columns whose
        // values are themselves objects/arrays — a table cell can't render nested data
        // (it degrades to "[object Object]"); that detail lives in VIEW RAW OUTPUT.
        const keyOrder = new Map<string, number>();
        const nonScalarCols = new Set<string>();
        rows.forEach(row => {
          Object.keys(row).forEach(k => {
            if (!keyOrder.has(k)) keyOrder.set(k, keyOrder.size);
            const cell = row[k];
            if (cell !== null && typeof cell === 'object') nonScalarCols.add(k);
          });
        });
        let colKeys = Array.from(keyOrder.keys())
          .filter(k => !nonScalarCols.has(k))
          .sort((a, b) => keyOrder.get(a)! - keyOrder.get(b)!);

        // Merge year+month into Period
        const hasYear = colKeys.includes('year');
        const hasMonth = colKeys.includes('month');
        if (hasYear && hasMonth) {
          colKeys = colKeys.filter(k => k !== 'year' && k !== 'month');
          colKeys.unshift('__period__');
        }

        // Totals for money columns
        const totals: Record<string, number | null> = {};
        colKeys.forEach(col => {
          if (col === '__period__') { totals[col] = null; return; }
          if (!isMoneyKey(col)) { totals[col] = null; return; }
          const sum = rows.reduce((acc, row) => {
            const v = row[col];
            return isNumeric(v) ? acc + parseNumeric(v) : acc;
          }, 0);
          totals[col] = sum;
        });

        return (
          <div key={key} className="px-6 pb-8">
            <div 
              className="text-[10.5px] font-semibold uppercase tracking-[0.12em] mb-3 mt-2" 
              style={{ color: 'var(--color-text-muted)' }}
            >
              {humanizeHeader(key)} ({totalRows})
            </div>

            <div>
              {/* Header row */}
              <div 
                className="grid gap-x-6 pb-2 border-b text-[10.5px] font-semibold uppercase tracking-[0.12em]"
                style={{ 
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-muted)',
                  gridTemplateColumns: `repeat(${colKeys.length}, minmax(0, 1fr))`
                }}
              >
                {colKeys.map(col => (
                  <div 
                    key={col} 
                    className={col === '__period__' ? 'text-left' : isMoneyKey(col) ? 'text-right' : 'text-left'}
                  >
                    {col === '__period__' ? 'Period' : humanizeHeader(col)}
                  </div>
                ))}
              </div>

              {/* Body rows */}
              {displayRows.map((row, idx) => (
                <div 
                  key={idx}
                  className="grid gap-x-6 py-2 border-b text-[13px] font-mono tabular-nums"
                  style={{ 
                    borderColor: 'var(--color-border-sub)',
                    color: 'var(--color-text-primary)',
                    gridTemplateColumns: `repeat(${colKeys.length}, minmax(0, 1fr))`,
                    minHeight: '36px'
                  }}
                >
                  {colKeys.map(col => {
                    if (col === '__period__') {
                      const p = formatPeriod(row.year as number | string, row.month as number | string);
                      return <div key={col} className="text-left self-center">{p}</div>;
                    }
                    const val = row[col];
                    if (val === null || val === undefined) {
                      return (
                        <div 
                          key={col} 
                          className={isMoneyKey(col) ? 'text-right' : 'text-left'}
                          style={{ color: 'var(--color-text-muted)' }}
                        >
                          —
                        </div>
                      );
                    }
                    const isMoney = isMoneyKey(col);
                    return (
                      <div 
                        key={col} 
                        className={`self-center ${isMoney ? 'text-right' : 'text-left'}`}
                      >
                        {isMoney && isNumeric(val)
                          ? formatCurrency(parseNumeric(val), { cents: true })
                          : isNumeric(val)
                            ? parseNumeric(val).toLocaleString()
                            : String(val)
                        }
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* Totals row */}
              <div 
                className="grid gap-x-6 pt-2 mt-2 border-t text-[13px] font-mono font-bold"
                style={{ 
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)',
                  gridTemplateColumns: `repeat(${colKeys.length}, minmax(0, 1fr))`
                }}
              >
                {colKeys.map(col => {
                  if (col === '__period__') {
                    return (
                      <div key={col} className="text-left text-[10.5px] uppercase tracking-[0.12em]" style={{ color: 'var(--color-text-muted)' }}>
                        Total
                      </div>
                    );
                  }
                  const total = totals[col];
                  if (total === null) {
                    return <div key={col} className="text-right" style={{ color: 'var(--color-text-muted)' }}>—</div>;
                  }
                  return <div key={col} className="text-right tabular-nums">{formatCurrency(total)}</div>;
                })}
              </div>

              {/* Show all toggle */}
              {showToggle && (
                <button
                  onClick={() => toggleTable(key)}
                  className="mt-3 text-[11px] uppercase tracking-wide"
                  style={{ color: 'var(--color-text-muted)' }}
                  onMouseEnter={e => { e.currentTarget.style.color = 'var(--color-brand)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'var(--color-text-muted)'; }}
                >
                  {isExpanded ? 'Show less' : `Show all ${totalRows} rows`}
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* Footer meta strip */}
      <div 
        className="px-6 py-4 border-t flex items-center justify-between text-[11px]"
        style={{ borderColor: 'var(--color-border)' }}
      >
        <span className="font-mono uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
          Engine {engineId}
        </span>
        <button
          onClick={() => setShowRaw(!showRaw)}
          className="uppercase tracking-wide"
          style={{ color: 'var(--color-text-muted)' }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--color-brand)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--color-text-muted)'; }}
        >
          {showRaw ? 'Hide raw output' : 'View raw output'}
        </button>
      </div>

      {/* Raw JSON disclosure */}
      {showRaw && (
        <div className="px-6 pb-6">
          <pre 
            className="p-4 rounded text-[11px] font-mono whitespace-pre-wrap break-all"
            style={{ 
              color: 'var(--color-text-muted)',
              backgroundColor: 'var(--color-bg-elevated)',
              border: '1px solid var(--color-border-sub)'
            }}
          >
            {JSON.stringify(outputs, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// PROPERTY VALUATION (RCV/ACV) — the independent first-party property engine
//
// Unlike the BI cascade engines, property data does not live in
// result.calculation_detail. The `property` engine output (RCV/ACV totals,
// recoverable-depreciation holdback, O&L, debris, coinsurance) is surfaced by
// the methodology endpoint, which also posture-tags each property method. This
// wrapper reads that endpoint directly so the reviewer sees the same numbers
// the methodology library exposes to the attorney.
// =============================================================================

interface PropertyValuation {
  rcv_total?: number;
  acv_total?: number;
  recoverable_depreciation?: number;
  ordinance_law_cost?: number;
  debris_removal?: number;
  coinsurance_factor?: number | null;
  rcv_recoverable?: boolean;
  methodology_notes?: string[];
}

interface PropertyMethod {
  method_id: string;
  label: string;
  category: string;
  posture: string;
  value?: number | null;
  authority?: string;
  notes?: string;
}

const POSTURE_BADGE: Record<string, React.CSSProperties> = {
  aggressive: { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' },
  conservative: { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' },
  conditional: { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' },
  neutral: { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' },
};

export function PropertyValuationDetail({ reportId, refreshKey }: EngineDetailProps) {
  const [valuation, setValuation] = useState<PropertyValuation | null>(null);
  const [methods, setMethods] = useState<PropertyMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<any>(`/api/v1/methodology/${reportId}`);
      setValuation((data?.property_valuation as PropertyValuation) || null);
      setMethods(
        ((data?.methods as PropertyMethod[]) || []).filter(
          (m) => m.category === 'property',
        ),
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to load property valuation');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (loading) return <SectionLoading />;
  if (error) return <SectionError message={error} onRetry={load} />;

  // No property loss on the claim yet → the same DAG-context placeholder.
  if (!valuation || !valuation.rcv_total) {
    return (
      <MissingEngineOutput
        engineId="property"
        hint="No property loss has been parsed onto this claim yet. Upload a Proof of Loss / Sworn Statement and run the property extractor to populate data.property_loss."
      />
    );
  }

  const v = valuation;
  const coinsurancePenalty =
    v.coinsurance_factor !== undefined &&
    v.coinsurance_factor !== null &&
    v.coinsurance_factor < 1.0;

  const summary: Array<{ label: string; value?: number; emphasize?: boolean }> = [
    { label: 'Replacement Cost (RCV)', value: v.rcv_total, emphasize: true },
    { label: 'Actual Cash Value (ACV)', value: v.acv_total },
    { label: 'Recoverable depreciation', value: v.recoverable_depreciation },
    { label: 'Ordinance or Law', value: v.ordinance_law_cost },
    { label: 'Debris removal', value: v.debris_removal },
  ];

  return (
    <div className="space-y-6" data-testid="engine-detail-property">
      <div className="border rounded-lg p-5 space-y-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="pb-3 border-b" style={{ borderColor: 'var(--color-border)' }}>
          {/* Name lives in the page <h1>; keep description only (K3 Defect B). */}
          <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
            {ENGINE_DESCRIPTIONS.property}
          </p>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {summary.map((s) => (
            <div key={s.label} className="flex flex-col gap-0.5">
              <dt className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-label)' }}>
                {s.label}
              </dt>
              <dd
                className={`font-mono tabular-nums ${
                  s.emphasize ? 'text-base font-semibold' : 'text-sm'
                }`}
                style={{ color: 'var(--color-text-primary)' }}
              >
                {formatCurrency(s.value)}
              </dd>
            </div>
          ))}
          <div className="flex flex-col gap-0.5">
            <dt className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-label)' }}>
              Coinsurance / condition of average
            </dt>
            <dd
              className={`text-sm font-mono tabular-nums ${
                coinsurancePenalty ? 'font-semibold' : ''
              }`}
              style={{ color: coinsurancePenalty ? 'var(--color-blocker)' : 'var(--color-text-primary)' }}
            >
              {v.coinsurance_factor === undefined || v.coinsurance_factor === null
                ? 'Not applicable'
                : coinsurancePenalty
                ? `${v.coinsurance_factor} (underinsurance penalty applies)`
                : `${v.coinsurance_factor} (no penalty)`}
            </dd>
          </div>
        </dl>
      </div>

      {methods.length > 0 && (
        <div className="border rounded-lg p-5 space-y-3" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
          <p className="text-sm font-semibold pb-2 border-b" style={{ color: 'var(--color-text-primary)', borderColor: 'var(--color-border)' }}>
            Recognized property methods
          </p>
          <ul className="divide-y divide-[color:var(--color-border-faint)]">
            {methods.map((m) => (
              <li key={m.method_id} className="py-3 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{m.label}</span>
                    <span
                      className="text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded-[2px] border"
                      style={POSTURE_BADGE[m.posture] || POSTURE_BADGE.neutral}
                    >
                      {m.posture}
                    </span>
                  </div>
                  {m.authority && (
                    <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>{m.authority}</p>
                  )}
                </div>
                <span className="text-sm font-mono tabular-nums flex-shrink-0" style={{ color: 'var(--color-text-primary)' }}>
                  {formatCurrency(m.value ?? undefined)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// PUBLIC ROUTER — pick the right detail view for an engine ID
// =============================================================================

export interface EngineDetailViewProps {
  engineId: EngineId;
  reportId: string;
  /** Bumped by the parent panel on every state transition; threaded through
      to `useEngineReportData` so the bundle re-fetches after approve/dispute/
      waive without a manual page reload. Phase 07.3c: this single prop
      retrofits all 11 routed engines for free. */
  refreshKey?: number;
  /** TS-FU12: threaded into each per-engine detail wrapper so the data fetch
   * picks the right namespace per mode (witness route for external, in-house
   * route for the rest). Without this, in-house users 401 on every non-merchant
   * engine. */
  mode?: ReviewPanelMode;
}

export function EngineDetailView({ engineId, reportId, refreshKey, mode }: EngineDetailViewProps) {
  switch (engineId) {
    case 'bottom_up':
      return <BottomUpDetail reportId={reportId} refreshKey={refreshKey} mode={mode} />;
    case 'enhanced_claim':
      return <EnhancedClaimDetail reportId={reportId} refreshKey={refreshKey} mode={mode} />;
    case 'merchant_revenue_augmentation':
      return <MerchantAugmentationDetail reportId={reportId} refreshKey={refreshKey} mode={mode} />;
    case 'dual_method':
      return <DynamicEngineDetail engineId="dual_method" reportId={reportId} refreshKey={refreshKey} mode={mode} />;
    case 'fair_settlement':
      return <FairSettlementDetail reportId={reportId} refreshKey={refreshKey} mode={mode} />;
    case 'case_strength':
      return <DynamicEngineDetail engineId="case_strength" reportId={reportId} refreshKey={refreshKey} mode={mode} />;
    case 'mitigation':
      return <MitigationDetail reportId={reportId} refreshKey={refreshKey} mode={mode} />;
    case 'impact':
      return <ImpactDetail reportId={reportId} refreshKey={refreshKey} mode={mode} />;
    case 'epi':
      return <EPIDetail reportId={reportId} refreshKey={refreshKey} mode={mode} />;
    case 'policy_deductions':
      return <PolicyDeductionsDetail reportId={reportId} refreshKey={refreshKey} mode={mode} />;
    case 'pv':
      return <PVDetail reportId={reportId} refreshKey={refreshKey} mode={mode} />;
    case 'property':
      return <PropertyValuationDetail reportId={reportId} refreshKey={refreshKey} mode={mode} />;
    case 'merchant_reconciliation':
      // merchant_reconciliation has its own bespoke wrapper in the page file
      return null;
    // Methodology-loop + Group A deterministic engines — dynamic key/value
    // rendering of whatever calculation_detail the engine produced.
    case 'projection_trend':
    case 'two_period':
    case 'capacity_ceiling':
    case 'non_recurring_item_detector':
    case 'catch_up_sales':
    case 'monthly_limit_cap':
    case 'extra_expense_cap':
    case 'third_party_damages':
    case 'tort_lost_profits':
    case 'l_recon':
      return <DynamicEngineDetail engineId={engineId} reportId={reportId} refreshKey={refreshKey} mode={mode} />;
    default: {
      // Exhaustiveness check — TS will error if a new engine is added
      const _exhaustive: never = engineId;
      void _exhaustive;
      return null;
    }
  }
}
